import React, { useContext, useState, useCallback } from 'react';
import { Loader2Icon, CheckCircle2Icon, UploadIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { S3Uploader, type UploadedFile } from '../s3uploader/S3Uploader';
import { ToolCallHandlersContext } from './chart-tool-call';
import ApiService from '@/services/api.service';
import BackgroundService from '@/services/bg.service';

interface FileUploadToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete' | 'interrupted'; reason?: string };
}

interface FilePart {
  id: string;
  file_id: string;
  part_identifier: string;
  headers: string[];
  sample_rows: string[][];
  record_count: number;
}

interface CreatedSource {
  srtId: string;
  srtName: string;
  columns: string[];
}

type Step = 'AWAITING_UPLOAD' | 'PROCESSING' | 'SELECT_TAB' | 'CREATING_SOURCE' | 'COMPLETE' | 'ERROR';

export const FileUploadToolCall: React.FC<FileUploadToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const handlers = useContext(ToolCallHandlersContext);
  const [step, setStep] = useState<Step>('AWAITING_UPLOAD');
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [fileParts, setFileParts] = useState<FilePart[]>([]);
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
  const [createdSource, setCreatedSource] = useState<CreatedSource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isRunning = status?.type === 'running';
  const isInterrupted = status?.type === 'interrupted';

  console.log('[FileUploadToolCall] render:', { step, statusType: status?.type, hasResult: !!result, resultPreview: typeof result === 'string' ? result.slice(0, 100) : result });

  // Parse purpose from args or from the interrupt value (result)
  let purpose = 'Upload a file';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    purpose = parsedArgs?.purpose || purpose;
  } catch (e) {
    // Ignore parse errors
  }

  // Parse the result to detect state
  // - awaiting_upload: interrupt value, show S3 uploader
  // - success: tool completed after resume, show completed state from result
  let isAwaitingUpload = false;
  let completedFromResult: { fileName: string; sourceName: string; columnCount: number } | null = null;
  if (result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed?.status === 'awaiting_upload') {
        isAwaitingUpload = true;
        if (parsed?.purpose) {
          purpose = parsed.purpose;
        }
      } else if (parsed?.status === 'success') {
        // Tool completed after resume — extract source info from result
        completedFromResult = {
          fileName: parsed.file_name || 'Uploaded file',
          sourceName: parsed.source_name || 'Data source',
          columnCount: parsed.column_count || 0,
        };
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  const selectedPart = fileParts.find(p => p.id === selectedPartId) || null;

  // Create SRT and load file part
  const createSource = useCallback(async (file: UploadedFile, part: FilePart, totalParts: number) => {
    setStep('CREATING_SOURCE');
    try {
      // Use the LLM-provided purpose as the source name if it's descriptive,
      // otherwise fall back to the filename
      const baseName = (purpose && purpose !== 'Upload a file') ? purpose : file.name;
      const srtName = totalParts > 1
        ? `${baseName} - ${part.part_identifier}`
        : baseName;

      // Create SourceRecordType
      const srtResp = await ApiService.getInstance().request('POST', '/sourcerecordtypes', {
        source_id: 'FLAT_FILE',
        name: srtName,
        description: '',
        resource_name: '',
        columns: part.headers.map(col => ({
          key: col,
          label: col,
          is_system_field: false,
          is_synthetic_field: false,
        })),
        composite_key: [],
        column_preferences: [],
      }) as { record: { id: string; name: string } };

      const srtId = srtResp.record.id;

      // Load the file part into the source
      const loadResp = await ApiService.getInstance().request('POST', `/files/${part.file_id}/parts/${part.id}/load`, {
        source_record_type_id: srtId,
        columns: part.headers,
      }) as { job_id: string };

      await BackgroundService.getInstance().waitForJob(loadResp.job_id);

      const source: CreatedSource = {
        srtId,
        srtName: srtResp.record.name,
        columns: part.headers,
      };
      setCreatedSource(source);
      setStep('COMPLETE');

      // Resume the graph with the source info
      // The interrupt() in the backend tool will return this value,
      // and the tool function will pass it as the tool result to the LLM.
      if (handlers?.onResumeGraph) {
        const columnsPreview = source.columns.length > 10
          ? source.columns.slice(0, 10).join(', ') + `, ... (${source.columns.length} total)`
          : source.columns.join(', ');
        handlers.onResumeGraph({
          status: 'success',
          file_name: file.name,
          source_record_type_id: source.srtId,
          source_name: source.srtName,
          columns: columnsPreview,
          column_count: source.columns.length,
        });
      }
    } catch (err) {
      console.error('[FileUploadToolCall] Source creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create data source');
      setStep('ERROR');
    }
  }, [handlers]);

  // Process file after upload completes
  const processFile = useCallback(async (file: UploadedFile) => {
    setStep('PROCESSING');
    try {
      const processResp = await ApiService.getInstance().request('POST', `/files/${file.id}/process`) as { job_id: string };
      const parts = await BackgroundService.getInstance().waitForJob(processResp.job_id) as FilePart[];

      if (!parts || parts.length === 0) {
        setError('No data found in file. The file may be empty or in an unsupported format.');
        setStep('ERROR');
        return;
      }

      setFileParts(parts);

      if (parts.length === 1) {
        // Single part — auto-select and proceed to source creation
        setSelectedPartId(parts[0].id);
        await createSource(file, parts[0], 1);
      } else {
        // Multiple parts — show tab selector
        setSelectedPartId(parts[0].id);
        setStep('SELECT_TAB');
      }
    } catch (err) {
      console.error('[FileUploadToolCall] Process error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process file');
      setStep('ERROR');
    }
  }, [createSource]);

  const handleUploadComplete = useCallback((file: UploadedFile) => {
    setUploadedFile(file);
    processFile(file);
  }, [processFile]);

  const handleTabContinue = useCallback(() => {
    if (!uploadedFile || !selectedPart) return;
    createSource(uploadedFile, selectedPart, fileParts.length);
  }, [uploadedFile, selectedPart, createSource, fileParts.length]);

  // ── Priority 1: Local state from active upload flow ──
  // These take priority over streaming status so the UI doesn't revert
  // to "Preparing file upload..." when the resume stream sets status to "running".

  // Error state
  if (step === 'ERROR') {
    return (
      <Card>
        <div className="p-3 space-y-2">
          {uploadedFile && (
            <div className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                <strong>{uploadedFile.name}</strong>
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
            <p className="text-sm m-0 text-destructive">{error}</p>
          </div>
        </div>
      </Card>
    );
  }

  // Processing state
  if (step === 'PROCESSING') {
    return (
      <Card>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Uploaded: <strong>{uploadedFile?.name}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-muted-foreground">
              Processing file...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Tab selection state (multi-sheet Excel)
  if (step === 'SELECT_TAB') {
    return (
      <Card>
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Uploaded: <strong>{uploadedFile?.name}</strong>
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm m-0 text-foreground font-medium">
              This file has {fileParts.length} sheets. Select which one to import:
            </p>
            <select
              value={selectedPartId || ''}
              onChange={(e) => setSelectedPartId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {fileParts.map((part) => (
                <option key={part.id} value={part.id}>
                  {part.part_identifier} ({part.record_count} rows, {part.headers.length} columns)
                </option>
              ))}
            </select>

            {selectedPart && (
              <div className="rounded border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50">
                        {selectedPart.headers.map((h) => (
                          <th key={h} className="px-2 py-1 text-left font-medium text-muted-foreground whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPart.sample_rows.slice(0, 3).map((row, i) => (
                        <tr key={i} className="border-t border-border">
                          {row.map((cell, j) => (
                            <td key={j} className="px-2 py-1 whitespace-nowrap text-muted-foreground max-w-[200px] truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button onClick={handleTabContinue} size="sm">
              Import this sheet
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Creating source state
  if (step === 'CREATING_SOURCE') {
    return (
      <Card>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Uploaded: <strong>{uploadedFile?.name}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-muted-foreground">
              Creating data source...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Complete state
  if (step === 'COMPLETE' && createdSource) {
    return (
      <Card>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Uploaded: <strong>{uploadedFile?.name}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Data source created: <strong>{createdSource.srtName}</strong>
            </p>
          </div>
          <p className="text-xs text-muted-foreground m-0">
            {createdSource.columns.length} columns loaded
          </p>
        </div>
      </Card>
    );
  }

  // ── Priority 2: Completed from result (handles component remount after resume) ──
  // If the tool completed after resume, the result contains success data.
  // This covers the case where React remounted the component (losing local state).
  if (completedFromResult && status?.type === 'complete') {
    return (
      <Card>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Uploaded: <strong>{completedFromResult.fileName}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Data source created: <strong>{completedFromResult.sourceName}</strong>
            </p>
          </div>
          {completedFromResult.columnCount > 0 && (
            <p className="text-xs text-muted-foreground m-0">
              {completedFromResult.columnCount} columns loaded
            </p>
          )}
        </div>
      </Card>
    );
  }

  // ── Priority 3: Running state (only when nothing has started yet) ──
  if (isRunning && step === 'AWAITING_UPLOAD') {
    return (
      <Card>
        <div className="p-3 bg-primary/10 border-b border-primary/20">
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Preparing file upload...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // ── Priority 4: Awaiting upload — show the uploader ──
  if ((isAwaitingUpload || isInterrupted) && step === 'AWAITING_UPLOAD') {
    return (
      <Card>
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2">
            <UploadIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              {purpose}
            </p>
          </div>
          <S3Uploader onUploadComplete={handleUploadComplete} />
          <p className="text-xs text-muted-foreground m-0">
            Supported formats: CSV, Excel (XLSX), TSV. Max file size: 250MB.
          </p>
        </div>
      </Card>
    );
  }

  // Fallback
  return (
    <Card>
      <div className="p-3 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-2">
          <UploadIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <p className="text-sm m-0 text-foreground">File upload</p>
        </div>
      </div>
    </Card>
  );
};
